/**
 * Resource Encoding Error Detection
 *
 * Passively detects same-origin resources that were transferred from the
 * server but failed to decode (e.g., ERR_CONTENT_DECODING_FAILED from
 * broken Brotli/gzip encoding served by R2 or CDN).
 *
 * Detection heuristic (PerformanceResourceTiming):
 *   transferSize > 0     → data was sent over the network
 *   encodedBodySize > 0  → server included a response body
 *   decodedBodySize === 0 → browser couldn't decompress the body
 *
 * Limitations:
 * - Only detects same-origin resources (cross-origin have zeroed timing data)
 * - SW importScripts failures are in the SW context and not visible here
 *   (those are caught by PwaUpdateBanner's 3s activation timeout instead)
 * - Only checks resources loaded before the idle callback fires; dynamically
 *   imported chunks that fail later are caught by Nuxt's chunk error handler
 *
 * Reports to Sentry as ResourceEncodingError.
 */
export default defineNuxtPlugin((nuxtApp) => {
  onNuxtReady(() => {
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => checkForEncodingErrors(nuxtApp), {
        timeout: 10_000,
      })
    } else {
      setTimeout(() => checkForEncodingErrors(nuxtApp), 5_000)
    }
  })
})

interface EncodingErrorEntry {
  url: string
  transferSize: number
  encodedBodySize: number
  decodedBodySize: number
  responseStatus: number
  initiatorType: string
  duration: number
}

function checkForEncodingErrors(nuxtApp: { [key: string]: unknown }) {
  if (typeof performance?.getEntriesByType !== 'function') return

  const entries = performance.getEntriesByType(
    'resource',
  ) as PerformanceResourceTiming[]
  const origin = window.location.origin

  const errors: EncodingErrorEntry[] = []

  for (const entry of entries) {
    // Only check same-origin (cross-origin resources have zeroed timing data)
    if (!entry.name.startsWith(origin)) continue

    // Encoding error signature:
    // Server sent data (transferSize > 0) with a body (encodedBodySize > 0)
    // but browser couldn't decode it (decodedBodySize === 0).
    // This happens when content-encoding (br/gzip) doesn't match actual bytes.
    if (
      entry.transferSize > 0 &&
      entry.encodedBodySize > 0 &&
      entry.decodedBodySize === 0
    ) {
      errors.push({
        url: entry.name,
        transferSize: entry.transferSize,
        encodedBodySize: entry.encodedBodySize,
        decodedBodySize: entry.decodedBodySize,
        responseStatus:
          'responseStatus' in entry
            ? (entry as { responseStatus: number }).responseStatus
            : -1,
        initiatorType: entry.initiatorType,
        duration: Math.round(entry.duration),
      })
    }
  }

  if (errors.length === 0) return

  const relativeUrls = errors.map((e) => e.url.replace(origin, ''))

  console.warn(
    `[Encoding Error] ${errors.length} resource(s) transferred but failed to decode:`,
    relativeUrls,
  )

  const sentry = nuxtApp.$sentry as
    | {
        captureException: (
          error: Error,
          context: Record<string, unknown>,
        ) => void
      }
    | undefined
  if (!sentry) return

  const error = new Error(
    `Resource encoding error: ${errors.length} resource(s) transferred but not decoded (${relativeUrls.join(', ')})`,
  )
  error.name = 'ResourceEncodingError'

  sentry.captureException(error, {
    level: 'error',
    tags: {
      'error-source': 'encoding-error-detection',
      'resource-count': String(errors.length),
    },
    extra: {
      resources: errors.map((e) => ({
        url: e.url.replace(origin, ''),
        transferSize: e.transferSize,
        encodedBodySize: e.encodedBodySize,
        decodedBodySize: e.decodedBodySize,
        responseStatus: e.responseStatus,
        initiatorType: e.initiatorType,
        duration: e.duration,
      })),
    },
  })
}
